import React, { useState } from 'react';
import clsx from 'clsx';
import { useAuth } from '@/contexts/AuthContext';
import type { Equipamento } from '../../../../models';
import { useEquipamentoMutations } from '../../../../hooks/data';
import {
  DETALHES_SITUACAO_ATIVO,
  SITUACAO_ATIVO_BADGE,
  SITUACAO_ATIVO_LABELS,
  SITUACOES_ATIVO,
  STATUS_POR_SITUACAO,
  type SituacaoAtivo,
} from './ativos.definitions';
import { detalheSituacaoDoAtivo, situacaoDoAtivo, type ContextoAtivos } from './ativos.utils';

function paraDataHora(timestamp: Equipamento['situacaoAlteradaEm']): string | null {
  if (!timestamp) return null;
  const data = timestamp.toDate();
  return `${data.toLocaleDateString('pt-BR')} às ${data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}`;
}

/**
 * Situação atual + alteração manual (discovery §6): automática pelas Movimentações/Estadias,
 * mas o SI pode ajustar quando precisar — sempre com motivo, que fica registrado (e no
 * histórico, na fase 2b).
 */
export default function AtivoSituacaoSecao({ equipamento, contexto }: { equipamento: Equipamento; contexto: ContextoAtivos }) {
  const { user } = useAuth();
  const { alterarSituacao } = useEquipamentoMutations();
  const atual = situacaoDoAtivo(equipamento);
  const [editando, setEditando] = useState(false);
  const [situacao, setSituacao] = useState<SituacaoAtivo>(atual);
  const [detalhe, setDetalhe] = useState('');
  const [motivo, setMotivo] = useState('');
  const [fornecedor, setFornecedor] = useState(equipamento.fornecedorManutencao ?? '');
  const faltaFornecedor = situacao === 'em_manutencao' && !fornecedor.trim();

  const detalheAtual = detalheSituacaoDoAtivo(equipamento, contexto);
  const detalhes = DETALHES_SITUACAO_ATIVO[situacao];
  const quandoManual = paraDataHora(equipamento.situacaoAlteradaEm);

  const cancelar = () => {
    setEditando(false);
    setSituacao(atual);
    setDetalhe('');
    setMotivo('');
    setFornecedor(equipamento.fornecedorManutencao ?? '');
  };

  const confirmar = () => {
    if (!user || !motivo.trim() || faltaFornecedor) return;
    alterarSituacao.mutate(
      {
        id: equipamento.id,
        statusCustodia: STATUS_POR_SITUACAO[situacao],
        situacaoDetalhe: detalhe || null,
        fornecedorManutencao: situacao === 'em_manutencao' ? fornecedor.trim() : null,
        motivo: motivo.trim(),
        ator: { id: user.collaboratorId ?? user.uid, name: user.displayName ?? user.email ?? '', avatar: null },
      },
      { onSuccess: cancelar }
    );
  };

  return (
    <section className="rounded-md border border-gray-200 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-400">Situação</p>
          <p className="mt-1 flex flex-wrap items-center gap-2">
            <span className={clsx('rounded-full px-2 py-0.5 text-sm font-semibold', SITUACAO_ATIVO_BADGE[atual])}>
              {SITUACAO_ATIVO_LABELS[atual]}
            </span>
            {detalheAtual && <span className="text-sm text-gray-600">{detalheAtual}</span>}
          </p>
          {equipamento.situacaoMotivo && quandoManual && (
            <p className="mt-1 text-xs text-gray-500">
              Alterada manualmente em {quandoManual}
              {equipamento.situacaoAlteradaPor?.name ? ` por ${equipamento.situacaoAlteradaPor.name}` : ''} — {equipamento.situacaoMotivo}
            </p>
          )}
        </div>
        {!editando && (
          <button type="button" className="btn-secondary text-sm" onClick={() => setEditando(true)}>
            Alterar situação
          </button>
        )}
      </div>

      {editando && (
        <div className="mt-3 space-y-3 border-t border-gray-100 pt-3">
          <p className="text-xs text-gray-500">
            A situação normalmente muda sozinha pelas Movimentações e Estadias. Use isto só quando precisar ajustar à mão.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nova situação</label>
              <select
                className="input-primary mt-1 w-full"
                value={situacao}
                onChange={(e) => {
                  setSituacao(e.target.value as SituacaoAtivo);
                  setDetalhe('');
                }}
              >
                {SITUACOES_ATIVO.map((s) => (
                  <option key={s} value={s}>
                    {SITUACAO_ATIVO_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
            {detalhes && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Detalhe</label>
                <select className="input-primary mt-1 w-full" value={detalhe} onChange={(e) => setDetalhe(e.target.value)}>
                  <option value="">—</option>
                  {Object.entries(detalhes).map(([valor, label]) => (
                    <option key={valor} value={valor}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {situacao === 'em_manutencao' && (
              <div>
                <label className="block text-sm font-medium text-gray-700">Fornecedor da manutenção *</label>
                <input
                  type="text"
                  className="input-primary mt-1 w-full"
                  placeholder="Onde está? Ex.: Oficina do SMART"
                  value={fornecedor}
                  onChange={(e) => setFornecedor(e.target.value)}
                />
              </div>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Motivo *</label>
            <textarea className="input-primary mt-1 w-full" rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary text-sm" onClick={cancelar}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn-primary text-sm"
              disabled={!motivo.trim() || faltaFornecedor || alterarSituacao.isPending}
              onClick={confirmar}
            >
              {alterarSituacao.isPending ? 'Salvando...' : 'Confirmar'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
